import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateText } from '../services/translationService';
import { supabase } from '../supabaseClient';
import {
    FaArrowLeft, FaEdit, FaTrash, FaFileInvoice, FaTools,
    FaCalendarAlt, FaUser, FaIdCard, FaStickyNote, FaTag,
    FaInfoCircle, FaFileAlt, FaTruck, FaChevronDown,
    FaCloudUploadAlt, FaImage, FaTimesCircle, FaSpinner,
    FaCommentDots, FaDownload, FaExpand, FaHistory, FaFileInvoiceDollar,
    FaTrophy, FaRocket, FaClock, FaExclamationTriangle,
    FaFilePdf, FaExternalLinkAlt, FaRedo, FaCube,
    FaPlus, FaChevronLeft, FaChevronRight, FaFolderOpen,
    FaThumbtack, FaSave, FaLock
} from 'react-icons/fa';
import confetti from 'canvas-confetti';
import WorkSheetGenerator from '../components/WorkSheetGenerator';
import PatientSheetGenerator from '../components/PatientSheetGenerator';
import QuoteGenerator from '../components/QuoteGenerator';
import InvoiceGenerator from '../components/InvoiceGenerator';
import DeliveryNoteGenerator from '../components/DeliveryNoteGenerator';
import CaseChat from '../components/CaseChat';
import RedoCaseModal from '../components/RedoCaseModal';
import Case3DFiles from '../components/Case3DFiles';
import laboratoryInfo from '../config';
import { getLotNumbers } from '../data/materialLotData';
import { getDeadlineAlert } from '../utils/deadlineUtils';
import RocketCelebration from '../components/RocketCelebration';
import { DentalImplantIcon, isCaseImplant } from '../utils/implantUtils';
import '../styles/CaseDossier.css';

const isPdfFile = (url) => {
    if (!url || typeof url !== 'string') return false;
    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
    return cleanUrl.endsWith('.pdf');
};

const getCleanFileName = (rawName, index = 0) => {
    if (!rawName) return `Document ${index + 1}`;
    let name = rawName.split('?')[0].split('#')[0];
    try { name = decodeURIComponent(name); } catch (_) {}
    // fiche_<timestamp>_<rand>_<originalName>
    const matchWithOrig = name.match(/^fiche_\d+(?:_[a-z0-9]+)?_(.+)$/i);
    if (matchWithOrig && matchWithOrig[1]) {
        return matchWithOrig[1].replace(/_/g, ' ');
    }
    // fiche_<timestamp>.<ext>
    const matchTimestamp = name.match(/^fiche_(\d+)\.([a-z0-9]+)$/i);
    if (matchTimestamp) {
        const date = new Date(parseInt(matchTimestamp[1], 10));
        if (!isNaN(date.getTime())) {
            return `Fiche du ${date.toLocaleDateString('fr-FR')} (${matchTimestamp[2].toUpperCase()})`;
        }
    }
    return name.replace(/_/g, ' ');
};

const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const formatDocDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } catch (_) {
        return '';
    }
};

function CaseDossier() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { orders, products, deleteOrder, updateOrder, clients, updateCaseStatus, activeDossierTab, setActiveDossierTab } = useData();
    const { isAdmin, isProducer, user, userRole } = useAuth();
    const { t, isChinese, translateStatus } = useLanguage();
    // Tab state: 'liste' (order items list) or 'fiche' (received sheet) or 'travail' (dedicated producer view)
    const [activeSubTab, setActiveSubTab] = useState(
        (activeDossierTab && activeDossierTab !== 'dossier')
            ? activeDossierTab
            : (isProducer ? 'travail' : 'liste')
    );
    const [showChatModal, setShowChatModal] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [translatedCaseNotes, setTranslatedCaseNotes] = useState(null);
    const [translatedItemNotes, setTranslatedItemNotes] = useState({});
    const [translatingNotes, setTranslatingNotes] = useState(false);
    const [showTranslatedNotes, setShowTranslatedNotes] = useState(true);
    
    // Post-it / Mémo Interne du gestionnaire
    const [isEditingMemo, setIsEditingMemo] = useState(false);
    const [memoText, setMemoText] = useState('');
    const [isSavingMemo, setIsSavingMemo] = useState(false);

    // Keep activeSubTab synced if parent/sidebar changes activeDossierTab
    useEffect(() => {
        if (activeDossierTab) {
            setActiveSubTab(activeDossierTab === 'dossier' ? 'liste' : activeDossierTab);
        }
    }, [activeDossierTab]);

    useEffect(() => {
        if (isProducer) {
            setActiveSubTab('travail');
        }
    }, [isProducer]);

    // Fiches & Documents state
    const [fiches, setFiches] = useState([]);
    const [selectedFicheIndex, setSelectedFicheIndex] = useState(0);
    const [ficheLoading, setFicheLoading] = useState(false);
    const [ficheUploading, setFicheUploading] = useState(false);
    const [ficheLightbox, setFicheLightbox] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const appendFileInputRef = useRef(null);

    const activeFiche = (fiches && fiches.length > 0)
        ? (fiches[selectedFicheIndex] || fiches[0])
        : null;
    const ficheUrl = activeFiche?.url || null;

    // Document dropdown menu
    const [showDocMenu, setShowDocMenu] = useState(false);
    const docMenuRef = useRef(null);

    const order = orders.find(o => o.id === orderId);
    const [showRedoModal, setShowRedoModal] = useState(false);

    // Redo tracking and linkage
    const isRedoCase = Boolean(
        order && (
            (order.notes && order.notes.includes('[REDO')) ||
            (Array.isArray(order.teethConfig) && order.teethConfig.some(line => line.isRedo || Number(line.quantity) === 0))
        )
    );
    const redoOfMatch = order?.notes ? order.notes.match(/\[REDO du cas\s+([^\]]+)\]/i) : null;
    const originalCaseNumber = redoOfMatch ? redoOfMatch[1].trim() : null;
    const originalCase = originalCaseNumber ? orders.find(o => (o.caseNumber || '').toLowerCase() === originalCaseNumber.toLowerCase()) : null;

    const currentCaseNum = order?.caseNumber || '';
    const derivedRedoCase = currentCaseNum ? orders.find(o => o.id !== order.id && o.notes && o.notes.toLowerCase().includes(`[redo du cas ${currentCaseNum.toLowerCase()}]`)) : null;

    const caseNumUpper = (order?.caseNumber || '').toUpperCase();
    const caseDigits = (caseNumUpper.match(/\d+/) || [])[0];
    const isEno100 = Boolean(order && (caseNumUpper === 'ENO-100' || caseNumUpper.endsWith('-100') || caseDigits === '100'));
    const isEno300 = Boolean(order && (caseNumUpper === 'ENO-300' || caseNumUpper.endsWith('-300') || caseDigits === '300'));
    const [rocketActive, setRocketActive] = useState(false);

    const triggerRocketCelebration = () => {
        setRocketActive(false);
        setTimeout(() => setRocketActive(true), 30);
    };

    const playCelebrationFanfare = () => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            const ctx = new AudioCtx();

            const notes = [
                { freq: 523.25, time: 0, dur: 0.15 },
                { freq: 659.25, time: 0.15, dur: 0.15 },
                { freq: 783.99, time: 0.30, dur: 0.15 },
                { freq: 1046.50, time: 0.45, dur: 0.45 },
                { freq: 880.00, time: 0.95, dur: 0.15 },
                { freq: 1046.50, time: 1.10, dur: 0.70 }
            ];

            notes.forEach(n => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.value = n.freq;

                gain.gain.setValueAtTime(0, ctx.currentTime + n.time);
                gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + n.time + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + n.time + n.dur);

                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(ctx.currentTime + n.time);
                osc.stop(ctx.currentTime + n.time + n.dur);
            });
        } catch (e) {
            console.warn('AudioContext unavailable:', e);
        }
    };

    const triggerCelebrationConfetti = () => {
        playCelebrationFanfare();

        confetti({
            particleCount: 160,
            spread: 110,
            origin: { y: 0.5 },
            colors: ['#ffd700', '#ff3e3e', '#00e5ff', '#10b981', '#a855f7', '#ff69b4'],
            scalar: 1.2
        });

        setTimeout(() => {
            confetti({
                particleCount: 100,
                angle: 60,
                spread: 80,
                origin: { x: 0, y: 0.65 },
                colors: ['#ffd700', '#ff4500', '#00e5ff', '#76ff03']
            });
        }, 300);

        setTimeout(() => {
            confetti({
                particleCount: 100,
                angle: 120,
                spread: 80,
                origin: { x: 1, y: 0.65 },
                colors: ['#ffd700', '#ff007f', '#7928ca', '#00dfd8']
            });
        }, 600);

        setTimeout(() => {
            confetti({
                particleCount: 200,
                spread: 160,
                origin: { y: 0.2 },
                ticks: 350,
                gravity: 0.7,
                scalar: 1.3
            });
        }, 900);
    };

    useEffect(() => {
        if (isEno300) {
            const timer = setTimeout(() => {
                triggerRocketCelebration();
            }, 400);
            return () => clearTimeout(timer);
        } else if (isEno100) {
            // Trigger celebratory confetti on view
            const timer = setTimeout(() => {
                triggerCelebrationConfetti();
            }, 400);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.caseNumber]);

    // Fetch unread count for this specific case
    useEffect(() => {
        if (!orderId || !user?.id) return;

        const fetchUnreadCount = async () => {
            try {
                const targetSender = isAdmin ? 'partner' : 'lab';
                const { data: allMessages } = await supabase
                    .from('case_messages')
                    .select('id')
                    .eq('order_id', orderId)
                    .eq('sender', targetSender);

                if (!allMessages || allMessages.length === 0) {
                    setUnreadCount(0);
                    return;
                }

                const { data: readMessages } = await supabase
                    .from('message_reads')
                    .select('message_id')
                    .eq('user_id', user.id);

                const readIds = new Set((readMessages || []).map(r => r.message_id));
                const unread = allMessages.filter(m => !readIds.has(m.id)).length;
                setUnreadCount(unread);
            } catch (err) {
                console.error('Error fetching unread count for case:', err);
            }
        };

        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 15000);
        return () => clearInterval(interval);
    }, [orderId, user?.id, isAdmin]);
    
    // Sync local memo state with order.internalNotes
    useEffect(() => {
        if (order) {
            setMemoText(order.internalNotes || '');
        }
    }, [order?.internalNotes, order?.id]);

    const handleSaveMemo = async () => {
        if (!order?.id) return;
        setIsSavingMemo(true);
        try {
            await updateOrder(order.id, { internalNotes: memoText });
            setIsEditingMemo(false);
        } catch (err) {
            console.error('Erreur sauvegarde mémo interne:', err);
            // Fallback garanti : enregistrement local immédiat
            try {
                if (memoText) {
                    localStorage.setItem(`eno_memo_${order.id}`, memoText);
                } else {
                    localStorage.removeItem(`eno_memo_${order.id}`);
                }
                order.internalNotes = memoText;
            } catch (_) {}
            setIsEditingMemo(false);
        } finally {
            setIsSavingMemo(false);
        }
    };

    const handleDeleteMemo = async (e) => {
        if (e) e.stopPropagation();
        if (!window.confirm("Voulez-vous vraiment effacer ce mémo interne ?")) return;
        setIsSavingMemo(true);
        try {
            await updateOrder(order.id, { internalNotes: '' });
            setMemoText('');
            setIsEditingMemo(false);
        } catch (err) {
            console.error('Erreur suppression mémo interne:', err);
        } finally {
            setIsSavingMemo(false);
        }
    };

    const insertMemoSnippet = (snippet) => {
        setMemoText(prev => {
            const trimmed = prev ? prev.trim() : '';
            return trimmed ? `${trimmed}\n${snippet}` : snippet;
        });
    };

    // Close doc dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (docMenuRef.current && !docMenuRef.current.contains(e.target)) {
                setShowDocMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load existing fiches on mount
    useEffect(() => {
        if (orderId) {
            loadFichesFromStorage();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    // Automatic translation of notes into Chinese when Chinese is active (placed before any early return)
    useEffect(() => {
        if (!order || !isChinese) return;

        const translateAll = async () => {
            setTranslatingNotes(true);
            try {
                if (order.notes && !translatedCaseNotes) {
                    const tr = await translateText(order.notes, 'zh-CN');
                    setTranslatedCaseNotes(tr);
                }

                if (order.teethConfig) {
                    const updated = { ...translatedItemNotes };
                    let itemsWithNotes = [];
                    if (Array.isArray(order.teethConfig)) {
                        itemsWithNotes = order.teethConfig.map((line, idx) => ({
                            key: line.teeth ? line.teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', ') : idx,
                            notes: line.notes
                        }));
                    } else if (typeof order.teethConfig === 'object') {
                        itemsWithNotes = Object.entries(order.teethConfig).map(([toothId, config]) => ({
                            key: toothId,
                            notes: config.notes
                        }));
                    }

                    for (const it of itemsWithNotes) {
                        if (it.notes && !updated[it.key]) {
                            const tr = await translateText(it.notes, 'zh-CN');
                            updated[it.key] = tr;
                        }
                    }
                    setTranslatedItemNotes(updated);
                }
            } catch (e) {
                console.error('Translation error:', e);
            } finally {
                setTranslatingNotes(false);
            }
        };

        translateAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.id, isChinese, order?.notes]);

    const loadFichesFromStorage = async () => {
        try {
            setFicheLoading(true);
            const { data: files, error } = await supabase.storage
                .from('fiches')
                .list(`orders/${orderId}`, { limit: 100, sortBy: { column: 'created_at', order: 'asc' } });

            if (error || !files || files.length === 0) {
                // If storage list is empty or errored, check if order already has fiches or ficheUrl
                if (order?.fiches && Array.isArray(order.fiches) && order.fiches.length > 0) {
                    setFiches(order.fiches);
                } else if (order?.ficheUrl && !order.ficheUrl.endsWith('/chat')) {
                    const fallbackDoc = {
                        id: 'fiche-0',
                        name: getCleanFileName(order.ficheUrl.split('/').pop(), 0),
                        fileName: order.ficheUrl.split('/').pop().split('?')[0],
                        url: order.ficheUrl,
                        storagePath: `orders/${orderId}/${order.ficheUrl.split('/').pop().split('?')[0]}`,
                        uploadedAt: order.updatedAt || order.createdAt
                    };
                    setFiches([fallbackDoc]);
                } else {
                    setFiches([]);
                }
                return;
            }

            // Exclude directories like 'chat' and non-file entries
            const validFiles = files.filter(f => f.name && f.name !== 'chat' && f.id !== null && f.name.includes('.'));
            if (validFiles.length === 0) {
                if (order?.fiches && Array.isArray(order.fiches) && order.fiches.length > 0) {
                    setFiches(order.fiches);
                } else {
                    setFiches([]);
                }
                return;
            }

            const loadedFiches = validFiles.map((f, idx) => {
                const { data: urlData } = supabase.storage
                    .from('fiches')
                    .getPublicUrl(`orders/${orderId}/${f.name}`);

                return {
                    id: f.id || `fiche-${idx}`,
                    name: getCleanFileName(f.name, idx),
                    fileName: f.name,
                    url: urlData?.publicUrl,
                    storagePath: `orders/${orderId}/${f.name}`,
                    uploadedAt: f.created_at || f.updated_at,
                    size: f.metadata?.size,
                    type: f.metadata?.mimetype
                };
            });

            setFiches(loadedFiches);
            // Also synchronize with DB so subsequent page views have document list immediately
            await updateOrder(orderId, {
                fiches: loadedFiches,
                ficheUrl: loadedFiches[0]?.url || null
            });
        } catch (err) {
            console.error('Erreur chargement fiches:', err);
            if (order?.fiches && Array.isArray(order.fiches) && order.fiches.length > 0) {
                setFiches(order.fiches);
            }
        } finally {
            setFicheLoading(false);
        }
    };

    const handleFicheUpload = async (e) => {
        const fileList = Array.from(e.target?.files || []);
        if (fileList.length === 0) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
        const validFiles = [];

        for (const file of fileList) {
            if (!allowedTypes.includes(file.type)) {
                alert(`Format non supporté pour "${file.name}". Utilisez : JPG, PNG, WebP, GIF ou PDF`);
                continue;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert(`Le fichier "${file.name}" est trop volumineux (max 10 MB)`);
                continue;
            }
            validFiles.push(file);
        }

        if (validFiles.length === 0) return;

        try {
            setFicheUploading(true);
            const newlyAdded = [];

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                const sanitizedName = file.name
                    .replace(/[^a-zA-Z0-9._-]/g, '_')
                    .replace(/_{2,}/g, '_');
                const fileName = `fiche_${Date.now()}_${Math.random().toString(36).substring(2, 6)}_${sanitizedName}`;
                const filePath = `orders/${orderId}/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('fiches')
                    .upload(filePath, file, { upsert: true });

                if (uploadError) {
                    console.error('Upload error for', file.name, uploadError);
                    const localUrl = URL.createObjectURL(file);
                    newlyAdded.push({
                        id: `local-${Date.now()}-${i}`,
                        name: file.name,
                        fileName: fileName,
                        url: localUrl,
                        storagePath: filePath,
                        uploadedAt: new Date().toISOString(),
                        size: file.size,
                        type: file.type
                    });
                    continue;
                }

                const { data: urlData } = supabase.storage
                    .from('fiches')
                    .getPublicUrl(filePath);

                if (urlData?.publicUrl) {
                    newlyAdded.push({
                        id: `fiche-${Date.now()}-${i}`,
                        name: file.name,
                        fileName: fileName,
                        url: urlData.publicUrl,
                        storagePath: filePath,
                        uploadedAt: new Date().toISOString(),
                        size: file.size,
                        type: file.type
                    });
                }
            }

            const updatedList = [...fiches, ...newlyAdded];
            setFiches(updatedList);
            setSelectedFicheIndex(updatedList.length - 1);
            await updateOrder(orderId, {
                fiches: updatedList,
                ficheUrl: updatedList[0]?.url || null
            });
        } catch (err) {
            console.error('Erreur upload fiche:', err);
            alert("Une erreur est survenue lors de l'envoi du document.");
        } finally {
            setFicheUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            if (appendFileInputRef.current) appendFileInputRef.current.value = '';
        }
    };

    const handleRemoveSingleFiche = async (indexToRemove) => {
        const doc = fiches[indexToRemove];
        if (!doc) return;

        const confirmMsg = fiches.length > 1
            ? `Supprimer le document "${doc.name}" ?\nLes autres documents associés au cas seront conservés.`
            : `Supprimer cette fiche reçue ?`;

        if (!window.confirm(confirmMsg)) return;

        try {
            if (doc.storagePath || doc.fileName) {
                const pathToRemove = doc.storagePath || `orders/${orderId}/${doc.fileName}`;
                await supabase.storage.from('fiches').remove([pathToRemove]);
            }
        } catch (err) {
            console.error('Erreur suppression fichier storage:', err);
        }

        const remaining = fiches.filter((_, idx) => idx !== indexToRemove);
        setFiches(remaining);
        if (selectedFicheIndex >= remaining.length) {
            setSelectedFicheIndex(Math.max(0, remaining.length - 1));
        }
        await updateOrder(orderId, {
            fiches: remaining,
            ficheUrl: remaining[0]?.url || null
        });
    };

    if (!order) {
        return (
            <div className="case-dossier-error">
                <h3>Cas non trouvé</h3>
                <p className="text-muted">Ce cas n'existe pas ou a été supprimé.</p>
                <button className="btn btn-primary mt-2" onClick={() => navigate('/jobs')}>
                    Retour à la liste des travaux
                </button>
            </div>
        );
    }

    const handleStatusChange = (newStatus) => {
        if (newStatus === 'Facturé' && !order.invoiceId) {
            if (window.confirm("Cet état nécessite la génération d'une facture pour apparaître en comptabilité. Voulez-vous générer la facture maintenant ?")) {
                navigate(`/invoices/new/${order.id}`);
                return;
            }
        }
        const now = new Date().toISOString();
        const updates = { status: newStatus };
        if (newStatus.toLowerCase() === 'livré' || newStatus.toLowerCase() === 'livre') {
            updates.deliveryDate = now;
        }
        updateOrder(order.id, updates);
    };

    const handleDelete = () => {
        if (window.confirm("Voulez-vous vraiment supprimer ce cas ?")) {
            deleteOrder(order.id);
            navigate('/jobs');
        }
    };

    const getStatusClass = (status) => {
        const s = status ? status.toLowerCase() : '';
        if (s === 'reçu' || s === 'recu') return 'status-received';
        if (s === 'facturé' || s === 'facture') return 'status-invoiced';
        if (s === 'livré') return 'status-completed';
        if (s === 'en attente' || s === 'terminé') return 'status-onhold';
        if (s === 'en cours' || s === 'production') return 'status-progress';
        return 'status-received';
    };

    const getTimelineEvents = () => {
        if (!order) return [];
        const rawEvents = [];

        if (Array.isArray(order.statusHistory) && order.statusHistory.length > 0) {
            order.statusHistory.forEach(item => {
                if (item && item.status) {
                    rawEvents.push({
                        status: item.status,
                        date: item.date,
                        label: item.label || null
                    });
                }
            });
        }

        // Ensure initial event exists
        if (rawEvents.length === 0 && order.createdAt) {
            rawEvents.push({
                status: 'Reçu',
                date: order.createdAt,
                label: 'Création du cas'
            });
        }

        // Ensure delivery event is reflected if deliveryDate exists
        if (order.deliveryDate && !rawEvents.some(e => (e.status || '').toLowerCase() === 'livré' || (e.status || '').toLowerCase() === 'livre')) {
            rawEvents.push({
                status: 'Livré',
                date: order.deliveryDate
            });
        }

        // Ensure invoice event is reflected if invoicedAt exists
        if (order.invoicedAt && !rawEvents.some(e => (e.status || '').toLowerCase() === 'facturé' || (e.status || '').toLowerCase() === 'facture')) {
            rawEvents.push({
                status: 'Facturé',
                date: order.invoicedAt
            });
        }

        // Ensure current status is present
        const currStatus = order.status || 'Reçu';
        const lastEvt = rawEvents[rawEvents.length - 1];
        if (!lastEvt || (lastEvt.status || '').toLowerCase() !== currStatus.toLowerCase()) {
            rawEvents.push({
                status: currStatus,
                date: order.updatedAt || new Date().toISOString()
            });
        }

        // Sort descending: most recent first (newest at index 0)
        const sorted = [...rawEvents].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

        // Deduplicate consecutive identical statuses in the sorted list
        const deduplicated = [];
        sorted.forEach((item, index) => {
            if (index === 0 || (item.status || '').toLowerCase() !== (deduplicated[deduplicated.length - 1].status || '').toLowerCase()) {
                deduplicated.push(item);
            }
        });

        // Mark the top item as current active state
        return deduplicated.map((item, idx) => ({
            ...item,
            isCurrent: idx === 0
        }));
    };

    const statusOptions = ['Reçu', 'Production', 'En attente', 'Livré', 'Facturé'];
    const deadlineAlert = getDeadlineAlert(order);

    const orderItems = [];
    if (order.teethConfig) {
        if (Array.isArray(order.teethConfig)) {
            order.teethConfig.forEach(line => {
                const product = products.find(p => p.id === line.productId);
                orderItems.push({
                    toothId: line.teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', '),
                    category: product ? product.category : 'Non défini',
                    type: product ? product.category : 'Non défini',
                    method: line.details?.methode || 'Standard',
                    material: line.details?.materiau || '-',
                    shade: line.details?.teinte || '-',
                    details: line.details || {},
                    name: product ? product.name : 'Produit inconnu',
                    notes: line.notes
                });
            });
        } else {
            Object.entries(order.teethConfig).forEach(([toothId, config]) => {
                const product = products.find(p => p.id === config.productId);
                orderItems.push({
                    toothId,
                    category: product ? product.category : 'Non défini',
                    type: product ? product.category : 'Non défini',
                    method: config.details?.methode || 'Standard',
                    material: config.details?.materiau || '-',
                    shade: config.details?.teinte || '-',
                    details: config.details || {},
                    name: product ? product.name : 'Produit inconnu',
                    notes: config.notes
                });
            });
        }
    }

    return (
        <div className="case-dossier-page">
            <div className="dossier-header">
                <div className="header-left">
                    <button className="btn-back" onClick={() => navigate('/jobs')}>
                        <FaArrowLeft />
                    </button>
                    <div className="case-title-area">
                        {isAdmin ? (
                            <div className="status-selector-wrapper">
                                <select
                                    className={`status-select-large ${getStatusClass(order.status)}`}
                                    value={order.status || 'Reçu'}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                >
                                    {statusOptions.map(opt => (
                                        <option key={opt} value={opt}>{translateStatus(opt)}</option>
                                    ))}
                                </select>
                                <FaChevronDown className="status-chevron" />
                            </div>
                        ) : (
                            <span className={`status-badge-readonly ${getStatusClass(order.status)}`}>
                                {translateStatus(order.status) || 'Reçu'}
                            </span>
                        )}
                        <h2>{isChinese ? `工单：${order.patientName}` : `Cas de ${order.patientName}`}</h2>
                        <span className="case-id">{isChinese ? '编号: ' : 'ID: '}{order.caseNumber || order.id.substring(0, 8).toUpperCase()}</span>
                        {isCaseImplant(order, products) && (
                            <span
                                className="badge ms-2 fw-bold d-inline-flex align-items-center"
                                style={{
                                    fontSize: '11px',
                                    backgroundColor: '#f3e8ff',
                                    color: '#7e22ce',
                                    border: '1px solid #d8b4fe',
                                    padding: '3px 8px',
                                    borderRadius: '6px'
                                }}
                                title="Cas avec Implantologie / Travail sur implant"
                            >
                                <DentalImplantIcon size={11} className="me-1" />
                                Implant
                            </span>
                        )}
                        {isEno300 && (
                            <span className="eno300-header-pill" onClick={triggerRocketCelebration} title="Dossier historique n°300 - Cliquez pour lancer la fusée !">
                                <FaRocket className="me-1" /> 300ème Cas ! 🚀
                            </span>
                        )}
                        {isEno100 && (
                            <span className="eno100-header-pill" onClick={triggerCelebrationConfetti} title="Dossier historique n°100 - Cliquez pour lancer les confettis !">
                                <FaTrophy className="me-1" /> 100ème Cas ! 🎉
                            </span>
                        )}
                        {order.deliveryDate && (
                            <span className="status-live-banner delivered">
                                <FaTruck className="me-1" />
                                Livré le {new Date(order.deliveryDate).toLocaleDateString('fr-FR')} à {new Date(order.deliveryDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                        {!order.deliveryDate && order.invoicedAt && (
                            <span className="status-live-banner invoiced">
                                <FaFileInvoiceDollar className="me-1" />
                                Facturé le {new Date(order.invoicedAt).toLocaleDateString('fr-FR')} à {new Date(order.invoicedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                        {isProducer && order.appointmentDate && (
                            <span className="badge bg-light text-dark border ms-2 py-1.5 px-2.5 d-none d-md-inline-flex align-items-center">
                                <FaCalendarAlt className="me-1 text-primary" />
                                Livraison : {new Date(order.appointmentDate).toLocaleDateString('fr-FR')}
                                {deadlineAlert && (
                                    <span className={`${deadlineAlert.badgeClass} ms-2`}>
                                        {deadlineAlert.shortLabel}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                    <button
                        className="btn-chat-quick"
                        onClick={() => {
                            setShowChatModal(true);
                            setUnreadCount(0);
                        }}
                        title="Ouvrir la discussion en plein écran"
                    >
                        <FaCommentDots />
                        {unreadCount > 0 && (
                            <span className="btn-chat-badge">{unreadCount}</span>
                        )}
                    </button>
                    {!isProducer && (
                        <button
                            type="button"
                            className={`btn-memo-quick ${order.internalNotes ? 'has-memo' : ''}`}
                            onClick={() => {
                                const el = document.getElementById('postit-memo');
                                if (el) {
                                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    if (!order.internalNotes) {
                                        setIsEditingMemo(true);
                                    }
                                }
                            }}
                            title={order.internalNotes ? `Mémo interne : ${order.internalNotes}` : "Ajouter un post-it / mémo pour ce cas"}
                        >
                            <FaThumbtack className="me-1 text-danger" size={12} />
                            <span>{order.internalNotes ? 'Post-it' : '+ Mémo'}</span>
                        </button>
                    )}
                </div>
                {isAdmin && (
                    <div className="header-actions">
                        <div className="doc-dropdown" ref={docMenuRef}>
                            <button
                                className="btn btn-sm btn-outline-primary doc-dropdown-trigger"
                                onClick={() => setShowDocMenu(!showDocMenu)}
                            >
                                <FaFileAlt className="me-1" /> Documents <FaChevronDown className={`doc-dropdown-chevron ${showDocMenu ? 'open' : ''}`} />
                            </button>
                            {showDocMenu && (
                                <div className="doc-dropdown-menu">
                                    <div className="doc-dropdown-item" onClick={() => setShowDocMenu(false)}>
                                        <WorkSheetGenerator order={order} buttonLabel="Fiche de travail" />
                                    </div>
                                    <div className="doc-dropdown-item" onClick={() => setShowDocMenu(false)}>
                                        <PatientSheetGenerator order={order} buttonLabel="Fiche Patient" />
                                    </div>
                                    <div className="doc-dropdown-item" onClick={() => setShowDocMenu(false)}>
                                        <QuoteGenerator order={order} buttonLabel="Devis" />
                                    </div>
                                    <div className="doc-dropdown-item" onClick={() => setShowDocMenu(false)}>
                                        <InvoiceGenerator order={order} buttonLabel="Facture" />
                                    </div>
                                    <div className="doc-dropdown-item" onClick={() => setShowDocMenu(false)}>
                                        <DeliveryNoteGenerator order={order} buttonLabel="Bon de Livraison" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-warning d-flex align-items-center gap-1 fw-bold text-dark rounded-3 px-3 shadow-sm border border-warning"
                            onClick={() => setShowRedoModal(true)}
                            title="Créer une fiche de travail à refaire (Redo à 0 €)"
                        >
                            <FaRedo className="me-1" /> Refaire ce cas
                        </button>
                        <button className="btn-icon-action" onClick={() => navigate(`/jobs/edit/${order.id}`)} title="Modifier">
                            <FaEdit />
                        </button>
                        <button className="btn-icon-action delete" onClick={handleDelete} title="Supprimer">
                            <FaTrash />
                        </button>
                    </div>
                )}
                {isProducer && (
                    <div className="header-actions">
                        <WorkSheetGenerator
                            order={order}
                            buttonLabel="Télécharger la Fiche de Travail"
                            className="btn btn-primary px-3 py-2 fw-bold shadow-sm d-flex align-items-center gap-2 rounded-3"
                            icon={<FaDownload className="me-1" />}
                        />
                    </div>
                )}
            </div>

            {/* Bannière d'alerte pour les cas Redo (à 0 €) */}
            {isRedoCase && (
                <div className="alert alert-warning d-flex align-items-center justify-content-between mx-3 mt-3 mb-0 shadow-sm border-warning rounded-3" style={{ background: '#fffbeb', borderLeft: '5px solid #f59e0b' }}>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-warning text-dark border border-dark border-opacity-10 px-2.5 py-1.5 fw-bold fs-6">
                            🔄 CAS À REFAIRE (REDO 0 €)
                        </span>
                        <span className="text-dark">
                            Ce travail est sous garantie (facturation 0,00 €).
                            {originalCaseNumber && (
                                <span className="ms-2">
                                    Cas d'origine : <strong className="font-monospace">{originalCaseNumber}</strong>
                                </span>
                            )}
                        </span>
                    </div>
                    {originalCase && (
                        <button 
                            type="button"
                            className="btn btn-sm btn-outline-dark fw-bold"
                            onClick={() => navigate(`/jobs/${originalCase.id}`)}
                            title={`Consulter le dossier d'origine ${originalCaseNumber}`}
                        >
                            Consulter le cas d'origine ({originalCaseNumber}) →
                        </button>
                    )}
                </div>
            )}

            {/* Bannière si un Redo a été créé à partir de ce cas */}
            {derivedRedoCase && (
                <div className="alert alert-info d-flex align-items-center justify-content-between mx-3 mt-3 mb-0 shadow-sm border-info rounded-3" style={{ background: '#eff6ff', borderLeft: '5px solid #3b82f6' }}>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary text-white px-2.5 py-1.5 fw-bold fs-6">
                            🔄 REFAIT GÉNÉRÉ
                        </span>
                        <span className="text-dark">
                            Une nouvelle fiche Redo a été créée pour ce cas : <strong className="font-monospace text-primary">{derivedRedoCase.caseNumber || 'Nouveau cas'}</strong>
                        </span>
                    </div>
                    <button 
                        type="button"
                        className="btn btn-sm btn-primary fw-bold"
                        onClick={() => navigate(`/jobs/${derivedRedoCase.id}`)}
                        title={`Consulter la fiche Redo ${derivedRedoCase.caseNumber}`}
                    >
                        Accéder à la fiche Redo ({derivedRedoCase.caseNumber}) →
                    </button>
                </div>
            )}

            {/* Bannière de célébration festive pour le cas ENO-300 */}
            {isEno300 && (
                <div className="eno300-dossier-banner" onClick={triggerRocketCelebration} title="Cliquez pour lancer la fusée !">
                    <div className="eno300-dossier-banner-content">
                        <span className="eno300-banner-rocket">🚀</span>
                        <div className="eno300-banner-text">
                            <strong className="eno300-banner-title">DOSSIER HISTORIQUE ENO-300 ! 🪐✨</strong>
                            <span className="eno300-banner-desc">Bravo pour le franchissement du 300ème cas du laboratoire E-novadent !</span>
                        </div>
                        <button
                            type="button"
                            className="btn-eno300-banner-burst"
                            onClick={(e) => {
                                e.stopPropagation();
                                triggerRocketCelebration();
                            }}
                        >
                            <FaRocket className="me-1" /> Lancer la fusée ! 🚀
                        </button>
                    </div>
                </div>
            )}

            {/* Bannière de célébration festive pour le cas ENO-100 */}
            {isEno100 && (
                <div className="eno100-dossier-banner" onClick={triggerCelebrationConfetti} title="Cliquez pour lancer les confettis !">
                    <div className="eno100-dossier-banner-content">
                        <span className="eno100-banner-trophy">🏆</span>
                        <div className="eno100-banner-text">
                            <strong>DOSSIER HISTORIQUE ENO-100 ! 🎉</strong>
                            <span>Félicitations pour le franchissement du 100ème cas du laboratoire E-novadent !</span>
                        </div>
                        <button
                            type="button"
                            className="btn-eno100-banner-burst"
                            onClick={(e) => {
                                e.stopPropagation();
                                triggerCelebrationConfetti();
                            }}
                        >
                            <FaRocket className="me-1" /> Confettis ! 🎊
                        </button>
                    </div>
                </div>
            )}

            {!isProducer ? (
                <div className="dossier-content">
                {/* Left Column: Metadata */}
                <div className="dossier-sidebar">
                    <div className="info-section">
                        <h4><FaIdCard className="me-2" /> Informations du Cas</h4>
                        <div className="info-item">
                            <label>ID de commande</label>
                            <span className="font-monospace">{order.caseNumber || order.id}</span>
                        </div>
                        <div className="info-item">
                            <label>Nom du patient</label>
                            <span className="fw-bold">{order.patientName}</span>
                        </div>
                        <div className="info-item">
                            <label>Réf. Patient</label>
                            <span>{order.patientReference || '-'}</span>
                        </div>
                        <div className="info-item">
                            <label>Responsable / Praticien</label>
                            <span>Dr. {order.practitionerName}</span>
                        </div>
                    </div>

                    <div className="info-section">
                        <h4><FaCalendarAlt className="me-2" /> Dates & Délais</h4>
                        <div className="info-item">
                            <label>Date de commande</label>
                            <span>{new Date(order.createdAt).toLocaleString('fr-FR')}</span>
                        </div>
                        <div className="info-item">
                            <label>Livraison demandée</label>
                            <span className="text-primary fw-bold">
                                {order.appointmentDate ? new Date(order.appointmentDate).toLocaleDateString('fr-FR') : 'Non définie'}
                            </span>
                        </div>
                        {order.deliveryDate && (
                            <div className="info-item highlight-delivery-date">
                                <label><FaTruck className="me-1 text-success" /> Livraison effective</label>
                                <span className="text-success fw-bold">
                                    {new Date(order.deliveryDate).toLocaleDateString('fr-FR')} à {new Date(order.deliveryDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        )}
                        {order.invoicedAt && (
                            <div className="info-item highlight-invoice-date">
                                <label><FaFileInvoiceDollar className="me-1 text-primary" /> Facturation</label>
                                <span className="text-primary fw-bold">
                                    {new Date(order.invoicedAt).toLocaleDateString('fr-FR')} à {new Date(order.invoicedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Post-it / Mémo Interne du gestionnaire */}
                    <div className="postit-card" id="postit-memo">
                        <div className="postit-pin" title="Post-it interne réservé au gestionnaire">
                            <FaThumbtack className="postit-pin-icon" />
                        </div>

                        <div className="postit-header">
                            <div className="postit-title-area">
                                <h4 className="postit-title">
                                    <span className="postit-badge-tape"></span>
                                    Mémo Interne
                                </h4>
                                <span className="postit-privacy-tag" title="Visible uniquement par le gestionnaire. Non partagé avec le sous-traitant et non imprimé.">
                                    <FaLock size={10} className="me-1" /> Privé
                                </span>
                            </div>

                            {!isEditingMemo && (
                                <div className="postit-header-btns">
                                    <button
                                        type="button"
                                        className="postit-action-btn edit"
                                        onClick={() => setIsEditingMemo(true)}
                                        title={order.internalNotes ? "Modifier ce post-it" : "Ajouter une note"}
                                    >
                                        <FaEdit className="me-1" />
                                        {order.internalNotes ? "Modifier" : "Écrire"}
                                    </button>
                                    {order.internalNotes && (
                                        <button
                                            type="button"
                                            className="postit-action-btn delete"
                                            onClick={handleDeleteMemo}
                                            title="Supprimer la note"
                                            disabled={isSavingMemo}
                                        >
                                            <FaTrash size={11} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {isEditingMemo ? (
                            <div className="postit-edit-mode">
                                <textarea
                                    className="postit-textarea"
                                    rows={4}
                                    value={memoText}
                                    onChange={(e) => setMemoText(e.target.value)}
                                    placeholder="Notez ici les informations pour ce cas (ex : commande d'implants passée, rappel praticien, étape spéciale...)"
                                    autoFocus
                                />
                                
                                <div className="postit-snippets-bar">
                                    <span className="snippets-hint">Raccourcis :</span>
                                    <button
                                        type="button"
                                        className="snippet-pill"
                                        onClick={() => insertMemoSnippet(`📅 ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} : `)}
                                    >
                                        + Date
                                    </button>
                                    <button
                                        type="button"
                                        className="snippet-pill"
                                        onClick={() => insertMemoSnippet("🔩 Implants commandés")}
                                    >
                                        + Implants
                                    </button>
                                    <button
                                        type="button"
                                        className="snippet-pill"
                                        onClick={() => insertMemoSnippet("📞 À rappeler Dr")}
                                    >
                                        + Appel Dr
                                    </button>
                                    <button
                                        type="button"
                                        className="snippet-pill"
                                        onClick={() => insertMemoSnippet("⚠️ À vérifier")}
                                    >
                                        + Attention
                                    </button>
                                    <button
                                        type="button"
                                        className="snippet-pill"
                                        onClick={() => insertMemoSnippet("✅ Fait")}
                                    >
                                        + Fait
                                    </button>
                                </div>

                                <div className="postit-edit-footer">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary postit-cancel-btn"
                                        onClick={() => {
                                            setMemoText(order.internalNotes || '');
                                            setIsEditingMemo(false);
                                        }}
                                        disabled={isSavingMemo}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-warning postit-save-btn text-dark fw-bold"
                                        onClick={handleSaveMemo}
                                        disabled={isSavingMemo}
                                    >
                                        {isSavingMemo ? (
                                            <>
                                                <FaSpinner className="fa-spin me-1" /> Sauvegarde...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="me-1" /> Enregistrer
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div
                                className={`postit-view-mode ${!order.internalNotes ? 'empty' : ''}`}
                                onClick={() => setIsEditingMemo(true)}
                                title="Cliquer pour modifier le post-it"
                            >
                                {order.internalNotes ? (
                                    <div className="postit-content-text">
                                        {order.internalNotes}
                                    </div>
                                ) : (
                                    <div className="postit-empty-hint">
                                        <FaPlus className="me-1" size={11} />
                                        <span>Cliquer pour ajouter un mémo / post-it sur ce cas...</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="info-section status-timeline-section">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h4 className="m-0"><FaHistory className="me-2 text-primary" /> Suivi & Évolution d'état</h4>
                            <span className="badge bg-light text-secondary border fw-normal" style={{ fontSize: '11px' }}>Plus récent en haut</span>
                        </div>
                        <div className="timeline-events-list">
                            {getTimelineEvents().map((evt, idx) => {
                                const isDeliv = (evt.status || '').toLowerCase() === 'livré' || (evt.status || '').toLowerCase() === 'livre';
                                return (
                                    <div
                                        key={idx}
                                        className={`timeline-event-item ${evt.isCurrent ? 'current-active-state latest' : 'past-history-state'} ${isDeliv ? 'is-delivered' : ''}`}
                                    >
                                        <div className={`timeline-dot ${evt.isCurrent ? 'pulse-active' : ''}`} />
                                        <div className="timeline-event-content">
                                            <div className="timeline-event-header">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className={`timeline-status-badge ${getStatusClass(evt.status)}`}>
                                                        {evt.status}
                                                    </span>
                                                    {evt.isCurrent && (
                                                        <span
                                                            className="badge bg-primary text-white fw-bold"
                                                            style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '6px', letterSpacing: '0.3px' }}
                                                        >
                                                            ACTUEL
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="timeline-event-date">
                                                    {evt.date ? `${new Date(evt.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${new Date(evt.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="info-section">
                        <h4><FaStickyNote className="me-2" /> Notes & Remarques</h4>
                        {order.notes && (
                            <div className="mb-3 p-2.5 rounded bg-warning bg-opacity-10 border border-warning text-dark font-monospace small" style={{ whiteSpace: 'pre-wrap' }}>
                                <strong>Remarque du cas :</strong>
                                <div className="mt-1">{order.notes}</div>
                            </div>
                        )}
                        <div className="notes-box technical-notes">
                            {(() => {
                                const notesList = orderItems.filter(item => item.notes);

                                if (notesList.length > 0) {
                                    return notesList.map((item, idx) => (
                                        <div key={idx} className="note-card-item mb-2 p-2 rounded bg-white border">
                                            <span className="badge bg-light text-primary me-2 fw-bold">Dent {item.toothId}</span>
                                            <div className="note-content mt-1 text-dark font-monospace small" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                                {item.notes}
                                            </div>
                                        </div>
                                    ));
                                }
                                if (!order.notes) {
                                    return "Aucune note technique enregistrée pour ce cas.";
                                }
                                return null;
                            })()}
                        </div>
                        {order.gamme && (
                            <div className="tags-area mt-3">
                                <span className="tag-badge"><FaTag size={10} className="me-1" /> {order.gamme.toUpperCase()}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column: Dynamic Tabs */}
                <div className="dossier-main">
                    <div className="tab-content" style={{ paddingTop: '10px' }}>
                        <div className="commande-tab animate__animated animate__fadeIn">
                                <div className="tab-sub-header">
                                    <button
                                        className={`sub-tab-btn ${activeSubTab === 'liste' ? 'active' : ''}`}
                                        onClick={() => setActiveSubTab('liste')}
                                    >
                                        Liste de commande
                                    </button>
                                    <button
                                        className={`sub-tab-btn ${activeSubTab === 'transfer' ? 'active' : ''}`}
                                        onClick={() => setActiveSubTab('transfer')}
                                    >
                                        <FaCube className="me-1" /> TransferEno (3D)
                                    </button>
                                    <button
                                        className={`sub-tab-btn ${activeSubTab === 'fiche' ? 'active' : ''}`}
                                        onClick={() => setActiveSubTab('fiche')}
                                    >
                                        <FaImage className="me-1" /> Fiches reçues {fiches.length > 0 && (
                                            <span className="badge bg-primary ms-1 px-2 py-0.5 rounded-pill" style={{ fontSize: '11px' }}>
                                                {fiches.length}
                                            </span>
                                        )}
                                    </button>
                                    {isProducer && (
                                        <button
                                            className={`sub-tab-btn ${activeSubTab === 'travail' ? 'active' : ''}`}
                                            onClick={() => setActiveSubTab('travail')}
                                        >
                                            <FaFileAlt className="me-1" /> Fiche de travail
                                        </button>
                                    )}
                                </div>

                                {activeSubTab === 'liste' && (
                                    <>
                                        {/* Desktop Table View */}
                                        <div className="table-responsive mt-3 d-none d-md-block">
                                            <table className="dossier-table">
                                                <thead>
                                                    <tr>
                                                        <th>Dent</th>
                                                        <th>Type</th>
                                                        <th>Méthode</th>
                                                        <th>Matériau</th>
                                                        <th>Teinte</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orderItems.length === 0 ? (
                                                        <tr><td colSpan="5" className="text-center py-4 text-muted">Aucun détail technique.</td></tr>
                                                    ) : (
                                                        orderItems.map((item, index) => (
                                                            <tr key={index}>
                                                                <td className="fw-bold">{item.toothId}</td>
                                                                <td>
                                                                    <div className="type-desc">{item.type}</div>
                                                                    <small className="text-muted">{item.name}</small>
                                                                </td>
                                                                <td>{item.method}</td>
                                                                <td>{item.material}</td>
                                                                <td className="fw-bold text-primary">{item.shade}</td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Mobile Responsive Cards View */}
                                        <div className="mobile-order-items mt-3 d-md-none">
                                            {orderItems.length === 0 ? (
                                                <div className="p-4 text-center text-muted card border-0 shadow-sm">Aucun détail technique.</div>
                                            ) : (
                                                orderItems.map((item, index) => (
                                                    <div key={index} className="order-item-mobile-card mb-3 p-3 bg-white rounded-3 border shadow-sm">
                                                        <div className="d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
                                                            <span className="badge bg-primary text-white fs-6 px-3 py-2">
                                                                Dent {item.toothId}
                                                            </span>
                                                            <span className="badge bg-primary-subtle text-primary fs-6 px-3 py-2 fw-bold">
                                                                Teinte: {item.shade}
                                                            </span>
                                                        </div>
                                                        <div className="mb-2">
                                                            <div className="fw-bold text-dark fs-6" style={{ wordBreak: 'break-word' }}>{item.type}</div>
                                                            <div className="text-muted small">{item.name}</div>
                                                        </div>
                                                        <div className="row g-2 pt-2 border-top text-center" style={{ fontSize: '0.85rem' }}>
                                                            <div className="col-6">
                                                                <span className="text-muted d-block small">Méthode</span>
                                                                <strong className="text-dark">{item.method}</strong>
                                                            </div>
                                                            <div className="col-6">
                                                                <span className="text-muted d-block small">Matériau</span>
                                                                <strong className="text-dark">{item.material}</strong>
                                                            </div>
                                                        </div>
                                                        {item.notes && (
                                                            <div className="mt-2 pt-2 border-top">
                                                                <small className="text-muted d-block fw-bold mb-1">Note :</small>
                                                                <div className="small font-monospace bg-light p-2 rounded text-dark" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                                                    {item.notes}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </>
                                )}

                                {isProducer && activeSubTab === 'travail' && (
                                    <div className="worksheet-tab-container mt-3 animate__animated animate__fadeIn" style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '30px', color: '#1e293b' }}>
                                        <div className="d-flex justify-content-end mb-3">
                                            <WorkSheetGenerator order={order} buttonLabel="Imprimer / Télécharger" />
                                        </div>
                                        {/* Header */}
                                        <div className="d-flex justify-content-between align-items-center mb-4 pb-3" style={{ borderBottom: '2px solid #1e293b' }}>
                                            <div>
                                                <h4 style={{ margin: 0, fontWeight: '800', color: '#1e293b' }}>{laboratoryInfo.name}</h4>
                                                <small className="text-muted fw-bold">LABORATOIRE DE PROTHÈSE DENTAIRE</small>
                                            </div>
                                            <div className="text-end">
                                                <h5 style={{ margin: 0, fontWeight: '800', color: '#3b82f6' }}>FICHE DE TRAVAIL</h5>
                                                <small className="font-monospace fw-bold">N° DOSSIER : {order.caseNumber || order.id.substring(0, 8).toUpperCase()}</small>
                                            </div>
                                        </div>

                                        {/* Info Grid */}
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6">
                                                <div style={{ border: '1px solid #e2e8f0', padding: '15px', borderRadius: '8px', height: '100%' }}>
                                                    <h6 className="fw-bold text-muted mb-2 small text-uppercase" style={{ letterSpacing: '0.05em' }}>Praticien</h6>
                                                    <p className="mb-1" style={{ color: '#1e293b' }}><strong>Nom :</strong> Dr. {order.practitionerName}</p>
                                                    <p className="mb-0" style={{ color: '#1e293b' }}><strong>Date commande :</strong> {new Date(order.createdAt).toLocaleDateString('fr-FR')}</p>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div style={{ border: '1px solid #e2e8f0', padding: '15px', borderRadius: '8px', height: '100%' }}>
                                                    <h6 className="fw-bold text-muted mb-2 small text-uppercase" style={{ letterSpacing: '0.05em' }}>Patient</h6>
                                                    <p className="mb-1" style={{ color: '#1e293b' }}><strong>Nom :</strong> {order.patientName}</p>
                                                    <p className="mb-0" style={{ color: '#1e293b' }}><strong>Gamme :</strong> {order.gamme ? (order.gamme.toLowerCase() === 'nova' ? 'Finition Nova' : order.gamme.toUpperCase()) : 'Finition Nova'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Technical Details Table */}
                                        <h6 className="fw-bold mb-3 pb-2 text-uppercase" style={{ borderBottom: '1px solid #e2e8f0', letterSpacing: '0.05em', color: '#475569' }}>Détails techniques</h6>
                                        <div className="table-responsive mb-4">
                                            <table className="table table-bordered mb-0" style={{ fontSize: '0.9rem' }}>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="text-center" style={{ width: '100px' }}>Dent</th>
                                                        <th>Travail à réaliser</th>
                                                        <th className="text-center" style={{ width: '100px' }}>Quantité</th>
                                                        <th>Teinte</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orderItems.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td className="text-center fw-bold align-middle">{item.toothId}</td>
                                                            <td className="align-middle">
                                                                <div className="fw-bold">{item.type}</div>
                                                                <small className="text-muted">{item.name}</small>
                                                            </td>
                                                            <td className="text-center fw-bold align-middle">{item.quantity || 1}</td>
                                                            <td className="align-middle fw-bold text-primary">{item.shade}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Notes & Traceability */}
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6">
                                                <div style={{ border: '1px solid #e2e8f0', padding: '15px', borderRadius: '8px', height: '100%', minHeight: '120px' }}>
                                                    <h6 className="fw-bold text-muted mb-2 small text-uppercase" style={{ letterSpacing: '0.05em' }}>Notes Laboratoire</h6>
                                                    {orderItems.filter(item => item.notes).map((item, idx) => (
                                                        <div key={idx} className="mb-2">
                                                            <span className="badge bg-light text-primary me-2">Dent {item.toothId}</span>
                                                            <span className="font-monospace small" style={{ color: '#1e293b' }}>{item.notes}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div style={{ border: '1px solid #cbd5e1', padding: '15px', borderRadius: '8px', backgroundColor: '#f8fafc', height: '100%' }}>
                                                    <h6 className="fw-bold mb-2 small text-uppercase" style={{ letterSpacing: '0.05em', color: '#1e3a8a' }}>Traçabilité des matériaux & Normes ISO / UE</h6>
                                                    <div style={{ fontSize: '0.8rem', color: '#334155', lineHeight: '1.8' }}>
                                                        {(() => {
                                                            const detectedLots = [];
                                                            const seenLotLabels = new Set();
                                                            orderItems.forEach(item => {
                                                                const lots = getLotNumbers(item.category, item.name, item.shade !== '-' ? item.shade : item.details?.teinte, item.notes);
                                                                lots.forEach(l => {
                                                                    if (!seenLotLabels.has(l.label)) {
                                                                        seenLotLabels.add(l.label);
                                                                        detectedLots.push(l);
                                                                    }
                                                                });
                                                            });
                                                            if (detectedLots.length > 0) {
                                                                return detectedLots.map((lot, idx) => (
                                                                    <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                                                ));
                                                            }
                                                            return (
                                                                <>
                                                                    Lot Disque Zircone / Métal Co-Cr : ___________________________<br />
                                                                    Lot Résine / Porcelaine : ___________________________<br />
                                                                    Lot Dents résine / Titane : ___________________________
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Quality signatures */}
                                        <div className="row g-3 pt-3" style={{ borderTop: '1px solid #e2e8f0' }}>
                                            <div className="col-4">
                                                <div className="text-center p-2" style={{ border: '1px dashed #cbd5e1', borderRadius: '8px', fontSize: '11px' }}>
                                                    <small className="fw-bold text-muted">Contrôle Qualité de Libération</small>
                                                    <div style={{ height: '30px' }}></div>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className="text-center p-2" style={{ border: '1px dashed #cbd5e1', borderRadius: '8px', fontSize: '11px' }}>
                                                    <small className="fw-bold text-muted">Visa Prothésiste / Fabricant</small>
                                                    <div style={{ height: '30px' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeSubTab === 'fiche' && (
                                    <div className="fiche-viewer-container mt-3 animate__animated animate__fadeIn">
                                        {ficheLoading ? (
                                            <div className="fiche-loading card p-5 text-center bg-white border rounded-3 shadow-xs">
                                                <FaSpinner className="spinner-icon mx-auto" />
                                                <p className="mt-2 mb-0 fw-semibold text-secondary">Chargement des fiches...</p>
                                            </div>
                                        ) : fiches.length > 0 ? (
                                            <div className="fiches-multi-container d-flex flex-column gap-3">
                                                {/* Header toolbar */}
                                                <div className="fiches-top-bar d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 bg-white border rounded-3 shadow-xs">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="p-2 rounded-2 bg-primary-subtle text-primary">
                                                            <FaFolderOpen size={18} />
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0 fw-bold text-dark">
                                                                Fiches & Documents reçus du cabinet
                                                            </h6>
                                                            <small className="text-muted">
                                                                {fiches.length} document{fiches.length > 1 ? 's' : ''} associé{fiches.length > 1 ? 's' : ''} à ce cas
                                                            </small>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary fw-semibold d-inline-flex align-items-center gap-1.5 rounded-pill px-3 shadow-xs"
                                                            onClick={() => appendFileInputRef.current?.click()}
                                                            disabled={ficheUploading}
                                                            title="Ajouter une autre fiche, ordonnance ou photo pour ce cas"
                                                        >
                                                            {ficheUploading ? (
                                                                <>
                                                                    <FaSpinner className="spinner-icon me-1" style={{ fontSize: '13px' }} /> Envoi...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FaPlus size={12} /> Ajouter un document
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Document tabs / selector list */}
                                                {fiches.length > 1 && (
                                                    <div className="fiches-nav-strip d-flex gap-2 overflow-auto pb-1">
                                                        {fiches.map((doc, idx) => {
                                                            const isSelected = idx === selectedFicheIndex;
                                                            const isPdf = isPdfFile(doc.url);
                                                            return (
                                                                <div
                                                                    key={doc.id || idx}
                                                                    onClick={() => setSelectedFicheIndex(idx)}
                                                                    className={`fiche-nav-pill d-flex align-items-center gap-2 px-3 py-2 rounded-3 border transition-all ${
                                                                        isSelected
                                                                            ? 'bg-primary text-white border-primary shadow-xs fw-bold'
                                                                            : 'bg-white text-dark border-slate-200 hover-bg-light'
                                                                    }`}
                                                                    style={{ cursor: 'pointer', whiteSpace: 'nowrap', minWidth: '160px', maxWidth: '240px' }}
                                                                    title={doc.name}
                                                                >
                                                                    <span className={isSelected ? 'text-white' : (isPdf ? 'text-danger' : 'text-primary')}>
                                                                        {isPdf ? <FaFilePdf size={16} /> : <FaImage size={16} />}
                                                                    </span>
                                                                    <span className="text-truncate flex-grow-1 small" style={{ maxWidth: '140px' }}>
                                                                        {doc.name || `Document ${idx + 1}`}
                                                                    </span>
                                                                    <span
                                                                        className={`badge ${isSelected ? 'bg-white text-primary' : 'bg-light text-muted border'} rounded-pill`}
                                                                        style={{ fontSize: '10px' }}
                                                                    >
                                                                        #{idx + 1}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Active Document Card Preview */}
                                                {activeFiche && (
                                                    <div className="fiche-modern-card bg-white rounded-3 border shadow-sm overflow-hidden">
                                                        {/* Header with Title and Action Buttons */}
                                                        <div className="fiche-modern-header d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 bg-light border-bottom">
                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                {isPdfFile(activeFiche.url) ? (
                                                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2.5 py-1.5 fw-bold d-inline-flex align-items-center gap-1">
                                                                        <FaFilePdf /> Document PDF
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2.5 py-1.5 fw-bold d-inline-flex align-items-center gap-1">
                                                                        <FaImage /> Image / Scan
                                                                    </span>
                                                                )}
                                                                <span className="fw-bold text-dark small">
                                                                    {activeFiche.name || `Document ${selectedFicheIndex + 1}`}
                                                                </span>
                                                                {activeFiche.size && (
                                                                    <span className="badge bg-secondary-subtle text-secondary small">
                                                                        {formatFileSize(activeFiche.size)}
                                                                    </span>
                                                                )}
                                                                {activeFiche.uploadedAt && (
                                                                    <small className="text-muted d-none d-sm-inline">
                                                                        • {formatDocDate(activeFiche.uploadedAt)}
                                                                    </small>
                                                                )}
                                                            </div>

                                                            <div className="fiche-action-buttons d-flex align-items-center gap-2 flex-wrap">
                                                                <button
                                                                    type="button"
                                                                    className="fiche-btn fiche-btn-secondary"
                                                                    onClick={() => setFicheLightbox(true)}
                                                                    title="Agrandir en plein écran"
                                                                >
                                                                    <FaExpand className="me-1" /> Plein écran
                                                                </button>
                                                                <a
                                                                    href={activeFiche.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="fiche-btn fiche-btn-secondary"
                                                                    title="Ouvrir dans un nouvel onglet"
                                                                >
                                                                    <FaExternalLinkAlt className="me-1" /> Ouvrir
                                                                </a>
                                                                <a
                                                                    href={activeFiche.url}
                                                                    download={activeFiche.name || 'document'}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="fiche-btn fiche-btn-primary"
                                                                    title="Télécharger le fichier"
                                                                >
                                                                    <FaDownload className="me-1" /> Télécharger
                                                                </a>
                                                                <button
                                                                    type="button"
                                                                    className="fiche-btn fiche-btn-danger"
                                                                    onClick={() => handleRemoveSingleFiche(selectedFicheIndex)}
                                                                    title="Supprimer ce document"
                                                                >
                                                                    <FaTrash className="me-1" /> Supprimer ce document
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Content Preview */}
                                                        <div className="fiche-modern-body p-3">
                                                            {isPdfFile(activeFiche.url) ? (
                                                                <div className="fiche-pdf-container position-relative rounded border overflow-hidden">
                                                                    <iframe
                                                                        src={`${activeFiche.url}#toolbar=1&navpanes=0`}
                                                                        title={activeFiche.name || "Fiche reçue PDF"}
                                                                        className="fiche-pdf-iframe"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className="fiche-image-container position-relative rounded border text-center p-3 bg-light-subtle"
                                                                    onClick={() => setFicheLightbox(true)}
                                                                    style={{ cursor: 'zoom-in' }}
                                                                    title="Cliquer pour agrandir en plein écran"
                                                                >
                                                                    <img
                                                                        src={activeFiche.url}
                                                                        alt={activeFiche.name || "Fiche reçue du praticien"}
                                                                        className="fiche-modern-img img-fluid rounded"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            const fallback = e.target.parentElement.querySelector('.fiche-error-fallback');
                                                                            if (fallback) fallback.style.display = 'block';
                                                                        }}
                                                                    />
                                                                    <div className="fiche-error-fallback" style={{ display: 'none', padding: '40px 20px' }}>
                                                                        <FaExclamationTriangle className="text-warning fs-1 mb-2" />
                                                                        <h6 className="fw-bold text-dark">L'aperçu direct du document ne peut pas être affiché.</h6>
                                                                        <p className="text-muted small mb-3">Le lien a peut-être expiré ou le format nécessite une ouverture directe.</p>
                                                                        <div className="d-flex justify-content-center gap-2">
                                                                            <a href={activeFiche.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                                                                                <FaExternalLinkAlt className="me-1" /> Ouvrir le fichier
                                                                            </a>
                                                                            <button onClick={() => loadFichesFromStorage()} className="btn btn-sm btn-outline-secondary">
                                                                                Recharger depuis le serveur
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="fiche-image-zoom-hint text-muted small mt-2">
                                                                        <FaExpand className="me-1 text-primary" /> Cliquez pour afficher en plein écran
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Dropzone to add more documents anytime */}
                                                <div
                                                    className={`fiche-upload-area-compact p-3 border border-2 border-dashed rounded-3 text-center transition-all ${isDragging ? 'is-dragging' : ''}`}
                                                    style={{ backgroundColor: '#f8fafc', cursor: 'pointer' }}
                                                    onClick={() => appendFileInputRef.current?.click()}
                                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                                    onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        setIsDragging(false);
                                                        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                                            handleFicheUpload({ target: { files: e.dataTransfer.files } });
                                                        }
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center justify-content-center gap-2 text-muted">
                                                        <FaCloudUploadAlt size={22} className="text-primary" />
                                                        <span className="fw-semibold small text-dark">
                                                            Glissez un document supplémentaire ici ou cliquez pour ajouter un autre fichier
                                                        </span>
                                                        <span className="badge bg-secondary-subtle text-secondary small">PDF, JPG, PNG</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Upload area when no documents exist yet */
                                            <div
                                                className={`fiche-upload-area ${isDragging ? 'is-dragging' : ''}`}
                                                onClick={() => fileInputRef.current?.click()}
                                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    setIsDragging(false);
                                                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                                        handleFicheUpload({ target: { files: e.dataTransfer.files } });
                                                    }
                                                }}
                                            >
                                                {ficheUploading ? (
                                                    <>
                                                        <FaSpinner className="spinner-icon" size={40} />
                                                        <p className="mt-3 fw-bold text-dark">Envoi en cours...</p>
                                                        <small className="text-muted">Téléversement sécurisé vers le serveur...</small>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="fiche-upload-icon-wrapper mb-3">
                                                            <FaCloudUploadAlt size={46} className="upload-icon text-primary" />
                                                        </div>
                                                        <h6 className="fw-bold text-dark mb-1">
                                                            {isDragging ? 'Déposez vos fichiers ici' : 'Cliquez ou glissez une ou plusieurs fiches ici'}
                                                        </h6>
                                                        <p className="text-muted small mb-3">
                                                            Fiche de prescription manuscrite, bon de commande papier, scan ou photo du cabinet (plusieurs fichiers acceptés)
                                                        </p>
                                                        <div className="d-flex justify-content-center gap-2 flex-wrap">
                                                            <span className="badge bg-info-subtle text-info border border-info-subtle">Plusieurs documents autorisés</span>
                                                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle">PDF</span>
                                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">JPG / JPEG</span>
                                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">PNG</span>
                                                            <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">Max 10 Mo</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept="image/*,.pdf"
                                            style={{ display: 'none' }}
                                            onChange={handleFicheUpload}
                                        />
                                        <input
                                            ref={appendFileInputRef}
                                            type="file"
                                            multiple
                                            accept="image/*,.pdf"
                                            style={{ display: 'none' }}
                                            onChange={handleFicheUpload}
                                        />
                                    </div>
                                )}

                                {activeSubTab === 'transfer' && (
                                    <div className="transfer-viewer-container mt-3">
                                        <Case3DFiles
                                            order={order}
                                            isAdmin={isAdmin}
                                            isProducer={isProducer}
                                        />
                                    </div>
                                )}

                            </div>
                    </div>
                </div>
            </div>
            ) : (
                <div className="producer-dossier-view container-fluid py-4" style={{ maxWidth: '1020px', margin: '0 auto' }}>
                    {/* Important Instructions Alert Banner */}
                    {(order.notes || orderItems.some(i => i.notes)) && (
                        <div className="producer-notice-box alert alert-warning border-2 border-warning shadow-sm mb-4 rounded-3 p-3 animate__animated animate__fadeIn">
                            <div className="d-flex align-items-start gap-3">
                                <div className="fs-3 text-warning">⚠️</div>
                                <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                        <h6 className="fw-bold text-dark mb-0 text-uppercase">
                                            {isChinese ? '重要制作要求与技工备注：' : 'Consignes & Remarques importantes pour la fabrication :'}
                                        </h6>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-dark fw-bold bg-white"
                                            style={{ fontSize: '11px', borderRadius: '14px', padding: '2px 8px' }}
                                            onClick={() => setShowTranslatedNotes(!showTranslatedNotes)}
                                        >
                                            {translatingNotes ? (isChinese ? '翻译中...' : 'Traduction...') : showTranslatedNotes ? (isChinese ? '📄 查看法文原文' : '📄 Voir original') : (isChinese ? '🌐 查看中文翻译' : '🌐 Traduire en chinois')}
                                        </button>
                                    </div>
                                    {order.notes && (
                                        <div className="p-2.5 rounded bg-white border border-warning-subtle text-dark fw-bold mb-2 fs-6" style={{ whiteSpace: 'pre-wrap' }}>
                                            {(showTranslatedNotes && translatedCaseNotes) ? translatedCaseNotes : order.notes}
                                        </div>
                                    )}
                                    {orderItems.filter(i => i.notes).map((item, idx) => (
                                        <div key={idx} className="small bg-white p-2 rounded border mb-1 d-flex align-items-center">
                                            <span className="badge bg-dark text-white me-2">{isChinese ? '牙位 ' : 'Dent '}{item.toothId}</span>
                                            <span className="fw-bold text-dark">
                                                {(showTranslatedNotes && translatedItemNotes[item.toothId || idx]) ? translatedItemNotes[item.toothId || idx] : item.notes}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* View Switcher: Fiche de travail vs Fiche reçue / Photo */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div className="d-flex gap-2">
                            <button
                                className={`btn btn-sm rounded-pill px-4 fw-bold ${activeSubTab === 'travail' ? 'btn-primary shadow-sm' : 'btn-white border text-secondary'}`}
                                onClick={() => setActiveSubTab('travail')}
                            >
                                <FaFileAlt className="me-2" /> {isChinese ? '生产制作工单' : 'Fiche de Travail (Production)'}
                            </button>
                            <button
                                className={`btn btn-sm rounded-pill px-4 fw-bold ${activeSubTab === 'transfer' ? 'btn-primary shadow-sm' : 'btn-white border text-secondary'}`}
                                onClick={() => setActiveSubTab('transfer')}
                            >
                                <FaCube className="me-2" /> {isChinese ? '3D 扫描模型 (STL)' : 'Fichiers 3D (TransferEno)'}
                            </button>
                            {fiches.length > 0 && (
                                <button
                                    className={`btn btn-sm rounded-pill px-4 fw-bold ${activeSubTab === 'fiche' ? 'btn-primary shadow-sm' : 'btn-white border text-secondary'}`}
                                    onClick={() => setActiveSubTab('fiche')}
                                >
                                    <FaImage className="me-2" /> {isChinese ? `原始照片 / 处方单 (${fiches.length})` : `Photos / Fiches reçues (${fiches.length})`}
                                </button>
                            )}
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <WorkSheetGenerator
                                order={order}
                                buttonLabel={isChinese ? '下载工单 PDF' : 'Télécharger la Fiche de Travail'}
                                className="btn btn-primary btn-sm rounded-pill px-3 fw-bold shadow-xs"
                                icon={<FaDownload className="me-1" />}
                            />
                        </div>
                    </div>

                    {/* TAB CONTENT */}
                    {activeSubTab === 'travail' ? (
                        <div className="producer-worksheet-paper shadow-sm animate__animated animate__fadeIn">
                            {/* Header of paper */}
                            <div className="d-flex justify-content-between align-items-center mb-4 pb-3" style={{ borderBottom: '2px solid #1e293b' }}>
                                <div>
                                    <h4 style={{ margin: 0, fontWeight: '800', color: '#1e293b' }}>{laboratoryInfo.name}</h4>
                                    <small className="text-muted fw-bold">LABORATOIRE DE PROTHÈSE DENTAIRE</small>
                                </div>
                                <div className="text-end">
                                    <h4 style={{ margin: 0, fontWeight: '800', color: '#2563eb' }}>{isChinese ? '义齿生产制作工单' : 'FICHE DE TRAVAIL'}</h4>
                                    <span className="font-monospace fw-bold badge bg-light text-dark border fs-6">
                                        {isChinese ? '工单编号：' : 'N° DOSSIER : '}{order.caseNumber || order.id.substring(0, 8).toUpperCase()}
                                    </span>
                                </div>
                            </div>

                            {/* Practitioner & Patient Cards */}
                            <div className="row g-3 mb-4">
                                <div className="col-md-6">
                                    <div className="producer-card-box">
                                        <h6 className="fw-bold text-muted mb-2 small text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                            {isChinese ? '门诊与医生信息' : 'Praticien'}
                                        </h6>
                                        <p className="mb-1 text-dark fs-6"><strong>{isChinese ? '医生：' : 'Nom :'}</strong> Dr. {order.practitionerName}</p>
                                        <p className="mb-0 text-muted small">
                                            <strong>{isChinese ? '下单日期：' : 'Date commande :'}</strong> {new Date(order.createdAt).toLocaleDateString(isChinese ? 'zh-CN' : 'fr-FR')}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="producer-card-box">
                                        <h6 className="fw-bold text-muted mb-2 small text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                            {isChinese ? '患者信息' : 'Patient'}
                                        </h6>
                                        <p className="mb-1 text-dark fs-6"><strong>{isChinese ? '患者：' : 'Nom :'}</strong> {order.patientName}</p>
                                        {order.patientReference && (
                                            <p className="mb-1 text-dark"><strong>{isChinese ? '参考编号：' : 'Référence :'}</strong> <span className="font-monospace fw-bold">{order.patientReference}</span></p>
                                        )}
                                        <p className="mb-0 text-primary fw-bold">
                                            <strong>{isChinese ? '要求交货日期：' : 'Livraison demandée :'}</strong> {order.appointmentDate ? new Date(order.appointmentDate).toLocaleDateString(isChinese ? 'zh-CN' : 'fr-FR') : (isChinese ? '未设定' : 'Non définie')}
                                            {deadlineAlert && (
                                                <span className={`${deadlineAlert.badgeClass} ms-2`}>
                                                    {deadlineAlert.shortLabel}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Technical Details Table */}
                            <h6 className="fw-bold mb-3 pb-2 text-uppercase d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #e2e8f0', letterSpacing: '0.05em', color: '#334155' }}>
                                <span>{isChinese ? '义齿制作技术明细' : 'Détails techniques des prothèses'}</span>
                                <span className="badge bg-secondary-subtle text-secondary">{orderItems.length} {isChinese ? '项' : 'élément(s)'}</span>
                            </h6>
                            <div className="table-responsive mb-4">
                                <table className="table table-bordered align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center" style={{ width: '120px' }}>{isChinese ? '牙位' : 'Dent'}</th>
                                            <th>{isChinese ? '制作类型及产品' : 'Travail à réaliser'}</th>
                                            <th className="text-center" style={{ width: '100px' }}>{isChinese ? '数量' : 'Quantité'}</th>
                                            <th className="text-center" style={{ width: '150px' }}>{isChinese ? '比色/色号' : 'Teinte'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orderItems.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="text-center align-middle">
                                                    <span className="badge bg-primary text-white fs-6 px-3 py-2">
                                                        {item.toothId}
                                                    </span>
                                                </td>
                                                <td className="align-middle">
                                                    <div className="fw-bold text-dark fs-6">{item.type}</div>
                                                    <div className="text-muted small">{item.name}</div>
                                                    {item.method && item.method !== 'Standard' && (
                                                        <span className="badge bg-light text-secondary border me-1 mt-1 small">{isChinese ? '工艺: ' : 'Méthode: '}{item.method}</span>
                                                    )}
                                                    {item.material && item.material !== '-' && (
                                                        <span className="badge bg-light text-secondary border mt-1 small">{isChinese ? '材料: ' : 'Matériau: '}{item.material}</span>
                                                    )}
                                                    {item.notes && (
                                                        <div className="producer-work-note-highlight mt-2">
                                                            <div className="d-flex align-items-center gap-1 text-warning-emphasis fw-bold small text-uppercase mb-1">
                                                                <FaExclamationTriangle className="text-warning me-1" /> {isChinese ? '制作要求：' : 'Consigne de travail :'}
                                                            </div>
                                                            <div className="producer-work-note-content">
                                                                {(showTranslatedNotes && translatedItemNotes[item.toothId || idx]) ? translatedItemNotes[item.toothId || idx] : item.notes}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-center fw-bold align-middle fs-6">{item.quantity || 1}</td>
                                                <td className="text-center align-middle">
                                                    <div className="producer-shade-pill">
                                                        <span className="producer-shade-label">{isChinese ? '色号' : 'TEINTE'}</span>
                                                        <span className="producer-shade-val">
                                                            {item.shade && item.shade !== '-' ? item.shade : 'Standard'}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Consignes & Notes de travail (Traçabilité retirée pour le sous-traitant) */}
                            <div className="producer-work-notes-card mb-4">
                                <div className="producer-work-notes-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <FaStickyNote className="text-warning fs-5" />
                                        <h6 className="fw-bold mb-0 text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                            {isChinese ? '特殊制作要求与技工备注' : 'Consignes & Notes de Travail'}
                                        </h6>
                                    </div>
                                    {orderItems.some(i => i.notes && i.notes.trim()) && (
                                        <span className="badge bg-warning text-dark fw-bold px-3 py-1">
                                            <FaExclamationTriangle className="me-1" /> {isChinese ? '重要制作要求' : 'Note de fabrication à respecter'}
                                        </span>
                                    )}
                                </div>

                                <div className="producer-work-notes-body p-3">
                                    {/* Item specific work notes */}
                                    {orderItems.filter(i => i.notes && i.notes.trim()).length > 0 && (
                                        <div className="mb-3">
                                            <div className="row g-3">
                                                {orderItems.filter(i => i.notes && i.notes.trim()).map((item, idx) => (
                                                    <div key={idx} className="col-12">
                                                        <div className="producer-specific-note-item">
                                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2 pb-2 border-bottom">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="badge bg-primary fs-6 px-3 py-1">
                                                                        {isChinese ? '牙位 ' : 'Dent '}{item.toothId}
                                                                    </span>
                                                                    <span className="fw-bold text-dark">
                                                                        {item.type} {item.name ? `• ${item.name}` : ''}
                                                                    </span>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle fs-6 px-3 py-1">
                                                                        {isChinese ? '比色: ' : 'Teinte : '}<strong>{item.shade && item.shade !== '-' ? item.shade : 'Standard'}</strong>
                                                                    </span>
                                                                    <span className="badge bg-light text-secondary border px-2 py-1">
                                                                        {isChinese ? '数量: ' : 'Qté : '}{item.quantity || 1}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="producer-instruction-highlight">
                                                                <div className="producer-instruction-tag">
                                                                    <FaExclamationTriangle className="me-1 text-warning" /> {isChinese ? '特殊制作要求：' : 'CONSIGNE PROTHÈSE :'}
                                                                </div>
                                                                <div className="producer-instruction-value">
                                                                    {(showTranslatedNotes && translatedItemNotes[item.toothId || idx]) ? translatedItemNotes[item.toothId || idx] : item.notes}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* General order notes */}
                                    {order.notes ? (
                                        <div className={orderItems.filter(i => i.notes && i.notes.trim()).length > 0 ? "pt-3 border-top" : ""}>
                                            <h6 className="fw-bold text-muted mb-2 small text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                                {isChinese ? '工单通用备注' : 'Remarques Générales du Cas'}
                                            </h6>
                                            <div className="p-3 rounded bg-white border border-secondary-subtle font-monospace text-dark fs-6" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                                {(showTranslatedNotes && translatedCaseNotes) ? translatedCaseNotes : order.notes}
                                            </div>
                                        </div>
                                    ) : (
                                        !orderItems.some(i => i.notes && i.notes.trim()) && (
                                            <div className="text-muted p-3 text-center bg-light rounded fst-italic">
                                                {isChinese ? '该工单无特殊制作备注。' : 'Aucune consigne particulière spécifiée pour ce cas.'}
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>

                            {/* Bottom action bar */}
                            <div className="d-flex justify-content-between align-items-center pt-3 border-top mt-4 flex-wrap gap-2">
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => navigate('/jobs')}
                                >
                                    <FaArrowLeft className="me-1" /> {isChinese ? '返回工单列表' : 'Retour à la liste'}
                                </button>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                        onClick={() => { setShowChatModal(true); setUnreadCount(0); }}
                                    >
                                        <FaCommentDots /> {isChinese ? '工单即时答疑' : 'Poser une question au labo'}
                                    </button>
                                    <WorkSheetGenerator
                                        order={order}
                                        buttonLabel={isChinese ? '打印/下载工单' : 'Imprimer / Télécharger la Fiche'}
                                        className="btn btn-primary btn-sm px-4 fw-bold shadow-sm"
                                        icon={<FaDownload className="me-1" />}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : activeSubTab === 'transfer' ? (
                        <div className="producer-worksheet-paper shadow-sm animate__animated animate__fadeIn p-4" style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <Case3DFiles order={order} isAdmin={false} isProducer={true} />
                        </div>
                    ) : (
                        /* Fiche reçue / Scans du cabinet */
                        <div className="producer-worksheet-paper shadow-sm animate__animated animate__fadeIn p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom flex-wrap gap-2">
                                <div>
                                    <h5 className="mb-0 fw-bold">Prescription originale / Photos reçues du cabinet</h5>
                                    {fiches.length > 0 && (
                                        <small className="text-muted">{fiches.length} document{fiches.length > 1 ? 's' : ''} disponible{fiches.length > 1 ? 's' : ''}</small>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {activeFiche && (
                                        <a
                                            href={activeFiche.url}
                                            download={activeFiche.name || 'document'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                        >
                                            <FaDownload className="me-1" /> Télécharger ({isPdfFile(activeFiche.url) ? 'PDF' : 'Image'})
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Document Selector Pills for Producer */}
                            {fiches.length > 1 && (
                                <div className="fiches-nav-strip d-flex gap-2 overflow-auto pb-2 mb-3">
                                    {fiches.map((doc, idx) => {
                                        const isSelected = idx === selectedFicheIndex;
                                        const isPdf = isPdfFile(doc.url);
                                        return (
                                            <button
                                                key={doc.id || idx}
                                                type="button"
                                                onClick={() => setSelectedFicheIndex(idx)}
                                                className={`btn btn-sm rounded-pill d-flex align-items-center gap-1.5 px-3 py-1.5 fw-semibold ${
                                                    isSelected ? 'btn-primary shadow-xs' : 'btn-light border text-dark'
                                                }`}
                                            >
                                                {isPdf ? <FaFilePdf /> : <FaImage />}
                                                <span className="text-truncate" style={{ maxWidth: '140px' }}>
                                                    {doc.name || `Document ${idx + 1}`}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {activeFiche ? (
                                <div className="text-center py-3">
                                    {isPdfFile(activeFiche.url) ? (
                                        <div className="fiche-pdf-container position-relative rounded border overflow-hidden shadow-sm">
                                            <iframe
                                                src={`${activeFiche.url}#toolbar=1&navpanes=0`}
                                                title={activeFiche.name || "Fiche reçue PDF"}
                                                className="fiche-pdf-iframe"
                                            />
                                        </div>
                                    ) : (
                                        <div className="fiche-thumbnail-wrapper" onClick={() => setFicheLightbox(true)} style={{ cursor: 'zoom-in' }}>
                                            <img
                                                src={activeFiche.url}
                                                alt={activeFiche.name || "Prescription du praticien"}
                                                className="img-fluid rounded shadow-sm"
                                                style={{ maxHeight: '600px', objectFit: 'contain' }}
                                            />
                                            <div className="text-muted small mt-2">
                                                <FaExpand className="me-1" /> Cliquez pour agrandir l'image en plein écran
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-5 text-muted">
                                    <FaImage size={48} className="text-muted opacity-25 mb-2 d-block mx-auto" />
                                    <p className="mb-0">Aucun scan ou photo additionnelle n'a été jointe pour ce cas.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Fiche Lightbox Modal */}
            {ficheLightbox && activeFiche && (
                <div className="fiche-lightbox-overlay" onClick={() => setFicheLightbox(false)}>
                    <div className="fiche-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="fiche-lightbox-close" onClick={() => setFicheLightbox(false)} title="Fermer">
                            <FaTimesCircle size={28} />
                        </button>

                        {/* Navigation arrows if multiple documents */}
                        {fiches.length > 1 && (
                            <>
                                <button
                                    className="fiche-lightbox-nav prev"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFicheIndex((selectedFicheIndex - 1 + fiches.length) % fiches.length);
                                    }}
                                    title="Document précédent"
                                >
                                    <FaChevronLeft size={22} />
                                </button>
                                <button
                                    className="fiche-lightbox-nav next"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFicheIndex((selectedFicheIndex + 1) % fiches.length);
                                    }}
                                    title="Document suivant"
                                >
                                    <FaChevronRight size={22} />
                                </button>
                            </>
                        )}

                        {isPdfFile(activeFiche.url) ? (
                            <iframe
                                src={activeFiche.url}
                                title={activeFiche.name || "Fiche reçue"}
                                style={{ width: '90vw', height: '88vh', border: 'none', borderRadius: '12px' }}
                            />
                        ) : (
                            <img
                                src={activeFiche.url}
                                alt={activeFiche.name || "Fiche reçue du praticien"}
                                className="fiche-lightbox-img"
                            />
                        )}
                        <div className="fiche-lightbox-actions d-flex gap-2 justify-content-center align-items-center mt-3 flex-wrap">
                            {fiches.length > 1 && (
                                <span className="text-white small fw-bold me-2">
                                    Document {selectedFicheIndex + 1} / {fiches.length}
                                </span>
                            )}
                            <a
                                href={activeFiche.url}
                                download={activeFiche.name || 'document'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-light btn-sm fw-bold px-4 rounded-pill shadow-sm"
                            >
                                <FaDownload className="me-2" /> Télécharger
                            </a>
                            <a
                                href={activeFiche.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-light btn-sm fw-bold px-4 rounded-pill"
                            >
                                <FaExternalLinkAlt className="me-2" /> Nouvel onglet
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Discussion Popup Modal */}
            {showChatModal && (
                <div className="chat-modal-overlay" onClick={() => setShowChatModal(false)}>
                    <div className="chat-modal-content animate__animated animate__zoomIn" onClick={(e) => e.stopPropagation()}>
                        <div className="chat-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <FaCommentDots className="text-primary fs-4" />
                                <h5 className="m-0 fw-bold">Discussion - {order.patientName}</h5>
                            </div>
                            <button className="chat-modal-close" onClick={() => setShowChatModal(false)} title="Fermer">
                                <FaTimesCircle />
                            </button>
                        </div>
                        <div className="chat-modal-body">
                            <CaseChat
                                orderId={orderId}
                                caseInfo={{
                                    caseNumber: order.caseNumber,
                                    patientName: order.patientName,
                                    practitionerName: order.practitionerName
                                }}
                                onMarkAsUnread={() => {
                                    setUnreadCount(1);
                                    setShowChatModal(false);
                                }}
                                onPendingReplyChange={(ordId, isPending) => {
                                    updateOrder(ordId, { priority: isPending ? 'pending_reply' : 'normal' });
                                }}
                                onClose={() => setShowChatModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de création d'un Redo (travail à refaire) */}
            {order && (
                <RedoCaseModal
                    isOpen={showRedoModal}
                    onClose={() => setShowRedoModal(false)}
                    order={order}
                    onSuccess={(createdOrder) => {
                        navigate(`/jobs/${createdOrder.id}`);
                    }}
                />
            )}

            {/* Animation de la fusée vers le ciel pour le 300ème cas */}
            <RocketCelebration
                active={rocketActive}
                onComplete={() => setRocketActive(false)}
                caseNumber={order?.caseNumber || 'ENO-300'}
            />
        </div>
    );
};

export default CaseDossier;
